import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { COLOR_TOOLTIP_BACKGROUND, exchangeRates } from 'src/const';
import { Host } from 'src/interfaces/idashboard';
import {
  convertCurrency,
  formatCurrency,
  getDummyMarketURL,
  getHeadingStyle,
  getVRMs,
} from 'src/utils/functions';
import { InfoTooltip } from '../../../HOC/Tooltips';
import { COLOR_TEXT_2 } from 'src/colors';
import { LoadingCard } from '../../../HOC/Cards';
import { infoIconButton, infoIconStyle } from 'src/App/Dashboards/Market/styles/style';
import BasicTable from 'src/App/Components/Tables/BasicTable';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { VisibleListingsProp } from 'src/interfaces/iproptypes';
import Top10ManagerCard from 'src/App/HOC/Cards/Top10ManagerCard';
import LockSharpIcon from '@material-ui/icons/LockSharp';

const Hosts = ({
  listings,
  isDisabled,
  currency,
  handleOnSampleClick,
  multipleMarkersCallLoading,
  search,
  user,
  matches,
  successAlert,
  handleMarketSidebarClick,
  handleSidebarListItemClick,
  setAdvancedTab,
  dashboardLayoutView,
  dummyMarket,
  userTailorMarketAccess,
}: {
  listings: VisibleListingsProp;
  isDisabled: boolean;
  currency: string;
  handleOnSampleClick: any;
  multipleMarkersCallLoading: Boolean;
  search: any;
  user: any;
  matches: boolean;
  successAlert: any;
  handleMarketSidebarClick: any;
  handleSidebarListItemClick: any;
  setAdvancedTab: any;
  dashboardLayoutView: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing';
  dummyMarket: any;
  userTailorMarketAccess: boolean
}) => {
  let top5HostsSupply: (string | number)[][] = [];
  const [openTop10ManagerModal, setOpenTop10ManagerModal] = useState<boolean>(false);
  const [listingHavingLicencePercentage, setListingHavingLicencePercentage] = useState<number>(0);
  const currentPath = window.location.pathname;
  let dummyMarketURL = getDummyMarketURL(dummyMarket);
  const isDemoMarket = currentPath.toLowerCase().includes(dummyMarketURL);
  const isAccessDenied =
    (isDisabled || (user.subscriptionPlan != 'expert' && dashboardLayoutView == 'map')) &&
    !isDemoMarket;

  useEffect(() => {
    if (listings.data.length) {
      let listingHavingLicenceCount = 0;
      for (let listing of listings.data) {
        if (listing.hasLicense) {
          listingHavingLicenceCount += 1;
        }
      }
      let listingHavingLicencePercentage = Math.round(
        (listingHavingLicenceCount / listings.data.length) * 100
      );
      setListingHavingLicencePercentage(listingHavingLicencePercentage);
    }
  }, [listings.data.length]);

  if (listings.data.length) {
    const top5Hosts: Host[] = getVRMs(
      listings.data,
      'totalNumberOfListings',
      isAccessDenied
    ).top10ManagerArray.slice(0, 5);
    top5HostsSupply = top5Hosts.map(host => [
      host.name,
      host.totalNumberOfListings,
      host.avgRating ? '★' + host.avgRating : 'No Reviews Yet',
      isAccessDenied
        ? Math.floor(Math.random() * 28950)
        : exchangeRates[currency].symbol +
          formatCurrency(convertCurrency(currency, host.revenue) + ''),
    ]);
  }

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent="center"
        item
        xs={12}
        spacing={1}
        sx={{
          padding: '5px',
          boxShadow: '0 4px 10px #0003',
          borderRadius: 1,
          overflow: 'auto',
          paddingRight: '5px',
        }}
      >
        <Grid item xs={12}>
          <Typography variant="body1" className="subHeadings">
            Top {top5HostsSupply.length} Hosts
            <InfoTooltip
              title={
                <Card>
                  <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                    <Grid container direction="column" alignItems="flex-start">
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        Top performing hosts based on total revenue
                      </Typography>
                    </Grid>
                  </CardContent>
                </Card>
              }
              enterTouchDelay={0}
              leaveTouchDelay={10000}
            >
              <IconButton style={infoIconButton}>
                <InfoOutlinedIcon style={infoIconStyle} />
              </IconButton>
            </InfoTooltip>
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ overflow: 'auto' }}>
          <BasicTable
            header={['Name', 'Listings', 'Rating', 'Revenue']}
            rows={top5HostsSupply}
            blurredColumns={isAccessDenied ? [3] : []}
            onButtonClick={handleOnSampleClick}
            dashboardLayoutView={dashboardLayoutView}
            userTailorMarketAccess={userTailorMarketAccess}
          />
        </Grid>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          item
          xs={12}
          sx={{ marginTop: '10px' }}
        >
          <Button onClick={() => setOpenTop10ManagerModal(true)}>View Top VRMs</Button>
        </Grid>
        <Grid item xs={12}>
          <Tooltip
            placement="top-start"
            title={
              listingHavingLicencePercentage <= 5 && !isAccessDenied
                ? `${listingHavingLicencePercentage} % of the listings are allowed to operate short-term rentals with a local license.`
                : ''
            }
          >
            <Typography sx={{ '&:hover': { cursor: 'pointer' }, fontSize: '12px' }}>
              {listingHavingLicencePercentage <= 5 && !isAccessDenied ? (
                'A local license may not be required to operate short-term rentals in this area.'
              ) : (
                <>
                  {isAccessDenied && (
                    <LockSharpIcon
                      style={{ fontSize: 15, color: 'inherit' }}
                      onClick={() =>
                        handleOnSampleClick(dashboardLayoutView == 'map' ? 'expert' : 'pro')
                      }
                    />
                  )}
                  {!isAccessDenied && listingHavingLicencePercentage}% of the listings are allowed
                  to operate short-term rentals with a local license.
                </>
              )}
            </Typography>
          </Tooltip>
        </Grid>
      </Grid>
      <Modal
        open={openTop10ManagerModal}
        onClose={() => {
          setOpenTop10ManagerModal(false);
        }}
      >
        <Container>
          <Box
            sx={{
              background: '#fff',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            {listings.loading ? (
              <LoadingCard height={150} />
            ) : (
              <Top10ManagerCard
                isDisabled={isAccessDenied}
                listings={listings}
                currentMarket={`${search.data.name}`}
                currency={currency}
                multipleMarkersCallLoading={multipleMarkersCallLoading}
                handleOnSampleClick={handleOnSampleClick}
                user={user}
                matches={matches}
                successAlert={successAlert}
                handleMarketSidebarClick={handleMarketSidebarClick}
                handleSidebarListItemClick={handleSidebarListItemClick}
                setAdvancedTab={setAdvancedTab}
              />
            )}
          </Box>
        </Container>
      </Modal>
    </>
  );
};

export default Hosts;
